// Per spec §7.12 + Q6a + §7.13:
// - shouldSilentRetryError: network-only (TypeError); HTTP 5xx skip silent retry
// - withSilentRetry: single retry on TypeError; same request body re-POSTed
//   (preserves message_id for backend idempotency dedup — 24h TTL per Phase 1
//   spec line 467, no race with the silent-retry window)
// - makeChatTransport: DefaultChatTransport with the silent-retry-wrapped fetch
//
// Substitution at the fetch boundary (NOT vi.mock('@ai-sdk/react')) per spec §7.13.
//
// Critical Gate 6 finding (verified pre-Task-1): @ai-sdk/react@3.0.170's
// regenerate() does NOT accept a reuseId flag. Silent retry for the network-blip
// case is therefore implemented INSIDE the custom fetch wrapper so that useChat
// never sees the failed POST and the request body — including the `id` field —
// is naturally re-POSTed identically.
//
// Body-stream gotcha: if init.body is a ReadableStream, fetch consumes it on
// the first attempt even when that attempt throws. Current callsites use
// JSON.stringify(...) (a string body), so the same `init` reference can safely
// be reused on retry. If a future callsite ever streams a body, the retry
// would need a body-clone strategy.

import { DefaultChatTransport } from 'ai'

export function shouldSilentRetryError(error: Error): boolean {
  return error instanceof TypeError
}

export function withSilentRetry(fetchImpl: typeof fetch): typeof fetch {
  return async (input, init) => {
    try {
      return await fetchImpl(input, init)
    } catch (err) {
      if (shouldSilentRetryError(err as Error)) {
        // Single retry. Re-POST identical (input, init) — body unchanged so
        // message_id is preserved and backend idempotency dedups if the
        // original request actually reached the server.
        return await fetchImpl(input, init)
      }
      throw err
    }
  }
}

export interface MakeTransportOptions {
  apiBase: string
  sessionId: string
  fetchImpl?: typeof fetch
}

export function makeChatTransport(opts: MakeTransportOptions) {
  return new DefaultChatTransport({
    api: `${opts.apiBase}/chat/turn`,
    fetch: withSilentRetry(opts.fetchImpl ?? fetch),
    // Function-form body per AI SDK Transport docs — re-evaluated on every
    // request rather than captured at hook init (Issue #7109 mitigation; see
    // https://ai-sdk.dev/docs/ai-sdk-ui/transport). Today session_id never
    // changes after the chat session is created, but function-form is the
    // canonical pattern and protects against the static-body class of bug
    // if we ever add genuinely dynamic per-request fields.
    //
    // Per-message id is NOT carried at the top level. The AI SDK includes
    // `messages[-1].id` in the default body shape; the backend derives the
    // per-turn id from there for idempotency dedup. Matches the canonical
    // vercel-labs/ai-sdk-preview-python-streaming reference.
    body: () => ({ session_id: opts.sessionId }),
  })
}
